"use client";
import { Search as SearchIcon } from 'lucide-react';
import { useEffect, useState, useRef } from "react";
import FloatingSearch from "../ui/FloatingSearch";
import DateRangePicker from "../date/DateRangePicker";
import SearchRedirectBtn from "../redirect_btn/SearchRedirectBtn";
import CitySelectbox from "../city/CitySelectbox";
import { useDateRange } from "../date/DateRangeContext";
import { usePathname } from "next/navigation";
import { ArrowLeft, Funnel } from "lucide-react";
import HomePageRedirectBtn from "../ui/HomePageRedirectBtn";
import { useAnalytics } from "@/hooks/useAnalytics";
import { SearchAnalytics } from "@/lib/analyticsName";

interface SearchProps {
  boxShadow?: boolean;
  onDateConfirm?: () => void;
  setShowFilter?: (show: boolean) => void;
  isSearchPage?: boolean;
  activeFiltersCount?: number;
}

const Search = ({
  boxShadow = true,
  onDateConfirm,
  setShowFilter,
  isSearchPage,
  activeFiltersCount = 0,
}: SearchProps) => {
  const { range, setRange } = useDateRange();
  const [isMounted, setIsMounted] = useState(false);
  const [showFloating, setShowFloating] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [selectedCity, setSelectedCity] = useState("");
  const pathname = usePathname();
  const isOnSearchPage = pathname.includes("/search");
  const { createClickHandler } = useAnalytics();

  // Create filter toggle handler with analytics
  const createFilterHandler = () => {
    return createClickHandler(
      () => setShowFilter?.(true),
      SearchAnalytics.Filter,
      SearchAnalytics.Category,
      {
        filter_type: 'mobile_filter',
        current_page: pathname,
        is_search_page: isOnSearchPage,
        has_date_range: !!(range?.startDate && range?.endDate)
      }
    );
  };

  // Create date confirm handler with analytics
  const createDateConfirmHandler = () => {
    return createClickHandler(
      () => onDateConfirm?.(),
      SearchAnalytics.DateSelect,
      SearchAnalytics.Category,
      {
        action_type: 'date_confirmation',
        current_page: pathname,
        is_search_page: isOnSearchPage,
        date_range: {
          start: range?.startDate,
          end: range?.endDate
        }
      }
    );
  };

  // Handler for SearchRedirectBtn onClick
  const handleDateConfirm = () => {
    const handler = createDateConfirmHandler();
    handler();
  };

  // Consolidated initialization effect
  useEffect(() => {
    setIsMounted(true);
    
    // Set default date range if not set (after 11 PM, default to tomorrow → day after)
    if (!range || !range.startDate || !range.endDate) {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      if (now.getHours() >= 23) {
        const dayAfter = new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);
        setRange({ startDate: tomorrow, endDate: dayAfter });
      } else {
        setRange({ startDate: now, endDate: tomorrow });
      }
    }

    // Load from localStorage
    const savedCity = localStorage.getItem("selectedCity");
    if (savedCity) {
      try {
        const parsed = JSON.parse(savedCity);
        if (
          parsed &&
          typeof parsed === "object" &&
          parsed.value &&
          parsed.label
        ) {
          setSelectedCity(parsed);
        }
      } catch (e) {
        console.error("Failed to parse selectedCity from localStorage", e);
      }
    }
  }, [range, setRange]);

  // Save to localStorage whenever selectedCity changes (only after mount)
  useEffect(() => {
    if (isMounted && selectedCity) {
      localStorage.setItem("selectedCity", JSON.stringify(selectedCity));
    }
  }, [selectedCity, isMounted]);

  // Observer for floating search
  useEffect(() => {
    if (!isMounted || !searchRef.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setShowFloating(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(searchRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  if (!isMounted) {
    return (
      <div
        className={`flex justify-between bg-white ${
          boxShadow && "shadow-[0_0_15px_rgba(0,0,0,0.15)]"
        } p-2 lg:p-4 rounded-full animate-pulse`}
      >
        <div className="bg-gray-200 rounded-4xl w-[280px] h-[40px]" />
        <div className="bg-gray-200 rounded-4xl w-[200px] h-[40px]" />
        <div className="bg-gray-200 rounded-4xl w-[120px] h-[40px]" />
      </div>
    );
  }

  return (
    <>
      {/* For Desktop */}
      <div className="hidden md:block">
        <div ref={searchRef}>
          <div
            className={`flex items-center gap-4 bg-white ${
              boxShadow && "shadow-[0_0_15px_rgba(0,0,0,0.15)]"
            } p-2 rounded-full`}
          >
            <CitySelectbox mobileColumns={1} desktopColumns={2}/>
            <div className="flex-1">
              <DateRangePicker />
            </div>
            <SearchRedirectBtn
              icon={<SearchIcon className="w-5 h-5"/>}
              onClick={isOnSearchPage ? () => handleDateConfirm() : undefined}
              analyticsName="search_button"
            >
              Search
            </SearchRedirectBtn>
          </div>
        </div>
        {showFloating && (
          <FloatingSearch searchRefData={searchRef} topPosition="80px" />
        )}
      </div>
      {/* For Mobile */}
      <div
        className={
          isSearchPage
            ? "bg-white fixed top-[46px] left-0 right-0 py-3 pl-2 pr-3 w-full md:hidden z-50"
            : "fixed top-24 left-0 right-0 py-3 pl-2 pr-3 w-full md:hidden z-50"
        }
      >
        <div className="flex items-center gap-1 relative min-w-0 w-full">
          {isSearchPage ? (
            <>
              <HomePageRedirectBtn className="flex-shrink-0">
                <ArrowLeft className="cursor-pointer" />
              </HomePageRedirectBtn>
              <div className="flex-1 min-w-0 overflow-visible flex flex-col">
                <DateRangePicker />
              </div>
              <button
                type="button"
                onClick={createFilterHandler()}
                className="relative flex-shrink-0 ml-0.5  cursor-pointer touch-manipulation flex items-center justify-center rounded hover:opacity-80"
                aria-label={`Filters${activeFiltersCount > 0 ? ` (${activeFiltersCount} active)` : ""}`}
              >
                <Funnel className="w-5 h-5" />
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#f05a24] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center pointer-events-none">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </>
          ) : (
            <>
              <DateRangePicker />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Search;
